/**
 * Shared fetch wrapper: base URL, JSON, timeout and errors. The *Api.ts files use it;
 * pages import those files (e.g. '@/helpers/rewardApi'), never fetch directly.
 */
import { API_BASE_URL } from './api';

const TIMEOUT_MS = 15_000;

/** Error returned by the backend: { error: { code, message, requestId } }. */
export class ApiError extends Error {
  /** HTTP status; 0 when the server could not be reached. */
  readonly status: number;
  /** Machine-readable code from the backend, e.g. NOT_FOUND, VALIDATION_ERROR. */
  readonly code: string;
  /** Quote this when reporting a problem; it matches the backend logs. */
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

/** BigInt values cannot be JSON-encoded; send them as strings like the backend does. */
const toJson = (body: unknown) =>
  JSON.stringify(body, (_key, value: unknown) => (typeof value === 'bigint' ? value.toString() : value));

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
      body: options.body !== undefined ? toJson(options.body) : undefined,
    });
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw controller.signal.aborted
      ? new ApiError(0, 'TIMEOUT', 'The server took too long to respond.')
      : new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the server. Check your connection.');
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 204) return undefined as T;
  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; requestId?: string } } | undefined)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'HTTP_ERROR',
      error?.message ?? `Request failed with status ${response.status}.`,
      error?.requestId,
    );
  }
  return payload as T;
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, { ...options, body }),
  delete: <T = void>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('DELETE', path, options),
};
